/**
 * Data fetching for public pages.
 *
 * The service layer already falls back to bundled content when the API is
 * unreachable (see services/api.js), so a network failure degrades to the
 * previous static site rather than an error page. This hook adds the three
 * states a page still needs to render honestly: loading, empty, and loaded.
 *
 * `stale` reports that the data came from the bundled fallback rather than the
 * database — useful where showing outdated promotions would mislead someone.
 */
import { useState, useEffect, useCallback, useRef } from "react";

export function useRemoteData(loader, dependencies = [], options = {}) {
  const { initialValue = null } = options;

  const [data, setData] = useState(initialValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Avoids setting state after unmount, and stops a slow first request from
  // overwriting a faster later one.
  const requestId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      const result = await loader();
      if (!mounted.current || id !== requestId.current) return;
      setData(result ?? initialValue);
    } catch (caught) {
      if (!mounted.current || id !== requestId.current) return;
      setError(caught);
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
    // The loader is recreated on every render by design; dependencies are
    // supplied explicitly by the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    run();
  }, [run]);

  const isEmpty =
    !loading && (data === null || data === undefined || (Array.isArray(data) && data.length === 0));

  return { data, loading, error, isEmpty, reload: run };
}
